import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { StoneXNumber } from '../../../StoneXMui';
import { AllocationModel, PositionModel, Side, Unit, UpdateAllocationRequest, UpdateDealTicketRequest, UpdatePositionModel, PositionType, ContractDate, GetCustomerSettingsResponse } from '../../../../Generated/Commodity-Management-Api';
import { HotTable } from '@handsontable/react-wrapper';
import Handsontable from 'handsontable';
import { CellChange, CellValue, ChangeSource } from 'handsontable/common';
import "handsontable/dist/handsontable.full.min.css";
import { DropdownEditor } from 'handsontable/editors';
import { ColumnSettings } from 'handsontable/settings';
import 'handsontable/styles/ht-theme-main.min.css';
import { months, monthToNumber } from '../../../../Helpers';
import { convertPrice, convertVolume } from '../../../../Helpers/UnitConversionHelper';
import { createRoot } from 'react-dom/client';
import { calculateWeightedPrice, sumVolume } from '../../../../Helpers/PositionAggregationHelper';
import PositionTable from '../Positions/ViewPositionsPage/PositionTable';
import { PositionReportColumn } from '../Positions/ViewPositionsPage/Types';

export interface EFPPositionsModalProps {
    open: boolean;
    onClose: () => void;
    positions: PositionModel[];
    allocations: AllocationModel[];
    customerSettings: GetCustomerSettingsResponse;
    units: Unit[];
    selectedDisplayUnit?: Unit;
    onSuccess: () => void;
}

interface ContractMonthGroup {
    contractDate: ContractDate;
    openVolume: number;
    weightedAveragePrice: number;
    bushelVolume?: number;
    longPositions: PositionModel[];
}

interface EfpRowData {
    contractDateLabel: string;
    contractDate: ContractDate;
    openVolume: number;
    weightedAveragePrice: number;
    efpQuantity: number | string;
    efpPrice: number | string;
    budgetDate: { month: string; year: number | string };
    supplier: string;
    purchaseOrder: string;
    unit: Unit;
    bushelVolume?: number;
    longPositions: PositionModel[];
}

const EFPPositionsModal: React.FC<EFPPositionsModalProps> = ({ open, onClose, positions, allocations, customerSettings, units, selectedDisplayUnit, onSuccess }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isSaving, setIsSaving] = useState(false);
    const [rowData, setRowData] = useState<EfpRowData[]>([]);
    const hotRef = useRef<any>(null);

    // Filter to buy and sell side futures
    const buyFuturesPositions = useMemo(() => {
        return positions.filter(p => p.side === Side.Buy);
    }, [positions]);

    const sellFuturesPositions = useMemo(() => {
        return positions.filter(p => p.side === Side.Sell);
    }, [positions]);

    // Columns to show in the PositionTable
    const positionTableColumns: PositionReportColumn[] = [
        'TradeDate',
        'ContractMonth',
        'Side',
        'Volume',
        'TradePrice',
        'Counterparty'
    ];

    // Group positions by contract date and calculate open volume and weighted average price for long positions
    const contractMonthGroups = useMemo((): ContractMonthGroup[] => {
        const groupMap = new Map<string, PositionModel[]>();

        // Group all positions by contract date
        positions.forEach(pos => {
            const key = `${pos.contractDate.year}-${pos.contractDate.month}`;
            const existing = groupMap.get(key) || [];
            existing.push(pos);
            groupMap.set(key, existing);
        });

        const groups: ContractMonthGroup[] = [];

        groupMap.forEach((positionsInMonth) => {
            // Calculate net volume (buys - sells)
            const buyPositions = positionsInMonth.filter(p => p.side === Side.Buy);
            const sellPositions = positionsInMonth.filter(p => p.side === Side.Sell);

            const buyVolume = sumVolume(buyPositions);
            const sellVolume = sumVolume(sellPositions);
            const openVolume = buyVolume - sellVolume;

            // Only include months with net long (open) volume
            if (openVolume > 0) {
                const weightedAveragePrice = calculateWeightedPrice(buyPositions);
                const firstPos = positionsInMonth[0];

                groups.push({
                    contractDate: firstPos.contractDate,
                    openVolume,
                    weightedAveragePrice,
                    bushelVolume: firstPos.positionPricingStrategy?.bushelVolume ?? undefined,
                    longPositions: buyPositions
                });
            }
        });

        // Sort by contract date
        return groups.sort((a, b) => {
            const dateA = a.contractDate.year! * 12 + a.contractDate.month!;
            const dateB = b.contractDate.year! * 12 + b.contractDate.month!;
            return dateA - dateB;
        });
    }, [positions]);

    useEffect(() => {
        if (open) {
            const initialRowData: EfpRowData[] = contractMonthGroups.map(group => {
                const contractDate = dayjs(`${group.contractDate.year}-${group.contractDate.month}-01`);
                const defaultBudgetDate = contractDate.subtract(1, 'month');

                // Dashboard already converts incoming position volumes/prices into
                // selectedDisplayUnit. Default to selectedDisplayUnit so the row
                // value and unit stay consistent.
                const initialUnit = selectedDisplayUnit ?? group.longPositions[0]?.unit;

                return {
                    contractDateLabel: `${months[group.contractDate.month! - 1]} ${group.contractDate.year}`,
                    contractDate: group.contractDate,
                    openVolume: group.openVolume,
                    weightedAveragePrice: group.weightedAveragePrice,
                    efpQuantity: '',
                    efpPrice: '',
                    budgetDate: {
                        month: months[defaultBudgetDate.month()],
                        year: defaultBudgetDate.year()
                    },
                    supplier: '',
                    purchaseOrder: '',
                    unit: initialUnit,
                    bushelVolume: group.bushelVolume,
                    longPositions: group.longPositions
                };
            });

            setRowData(initialRowData);
        }
    }, [open, contractMonthGroups, selectedDisplayUnit]);

    class CustomDropdown extends DropdownEditor {
        limitDropdownIfNeeded(spaceAvailable: any, dropdownHeight: any): void {
            super.limitDropdownIfNeeded(spaceAvailable, dropdownHeight);
            const newHeight = (this.cellProperties?.source?.length ?? 0) * 40;
            this.htEditor.updateSettings({ height: newHeight + 4, width: '100%' });
        }

        flipDropdown(dropdownHeight: number) {
            const dropdownStyle = this.htEditor.rootElement.style;
            dropdownStyle.position = 'absolute';
            dropdownStyle.top = '0px';
        }
    }

    const priceRenderer = function (_instance: any, TD: any, _row: any, _col: any, prop: any, value: any, cellProperties: any) {
        if (!(TD as any).__root) {
            (TD as any).__root = createRoot(TD);
        }
        const element = <StoneXNumber number={value} decimals={3} ifNoNumber='' className={cellProperties.readOnly ? 'htDimmed' : undefined} />;
        (TD as any).__root.render(element);

        if (cellProperties.className) {
            cellProperties.className.split(/\s+/).forEach((cls: string) => {
                if (cls) TD.classList.add(cls);
            });
        }
    };

    function numericValidator(value: CellValue, callback: (valid: boolean) => void) {
        const number = parseFloat(value as string);
        const isValidVolume = !isNaN(number) && number > 0;

        if (value === null || value === undefined || value === '' || !isValidVolume) {
            callback(false);
        } else {
            callback(true);
        }
    }

    Handsontable.renderers.registerRenderer('priceRenderer', priceRenderer);
    Handsontable.editors.registerEditor("customDropdown", CustomDropdown);
    Handsontable.validators.registerValidator('numericValidator', numericValidator);

    const sortedUnitOptions = useMemo(() => {
        return [...(units ?? [])].sort((a, b) => a.localeCompare(b));
    }, [units]);

    const columns: ColumnSettings[] = [
        {
            data: 'contractDateLabel',
            readOnly: true,
        },
        {
            data: 'openVolume',
            className: 'numeric',
            readOnly: true,
        },
        {
            data: 'weightedAveragePrice',
            className: 'numeric',
            renderer: priceRenderer,
            readOnly: true,
        },
        {
            data: 'unit',
            type: 'dropdown',
            editor: 'customDropdown',
            source: sortedUnitOptions,
            validator: (value, callback) => callback(!!value && sortedUnitOptions.includes(value as Unit))
        },
        {
            data: 'efpQuantity',
            className: 'numeric',
            validator: 'numericValidator',
        },
        {
            data: 'efpPrice',
            className: 'numeric',
            renderer: priceRenderer,
        },
        {
            data: 'budgetDate.month',
            type: 'dropdown',
            editor: 'customDropdown',
            source: months,
            validator: (value, callback) => callback(!!value && months.includes(value))
        },
        {
            data: 'budgetDate.year',
            className: 'numeric',
        },
        {
            data: 'supplier',
            type: 'dropdown',
            editor: 'customDropdown',
            source: (allocations ?? []).map(alloc => alloc.counterparty.counterpartyName ?? '')
        },
        {
            data: 'purchaseOrder',
            className: 'numeric'
        }
    ];

    const handleProceed = async () => {
        setIsSaving(true);
        const promises = [];

        for (const row of rowData) {
            const efpQuantity = Number(row.efpQuantity);
            const efpPrice = Number(row.efpPrice) || row.weightedAveragePrice;
            const futuresPrice = Number(row.weightedAveragePrice)
            const budgetMonth = monthToNumber(row.budgetDate.month as string);
            const budgetYear = Number(row.budgetDate.year);
            const supplierName = row.supplier;
            const purchaseOrder = row.purchaseOrder;

            if (efpQuantity > 0 && supplierName && budgetMonth && budgetYear) {
                const promise = async () => {
                    const selectedAllocation = allocations.find(a => a.counterparty.counterpartyName === supplierName);
                    if (!selectedAllocation) return;

                    const firstPosition = row.longPositions[0];
                    const newBudgetDate = { year: budgetYear, month: budgetMonth, day: 1 };
                    const newContractDate = { year: row.contractDate.year, month: row.contractDate.month, day: 1 };

                    // Create a new deal ticket with sell side for the offsetting future
                    const offsettingFuture: UpdatePositionModel = {
                        positionId: undefined,
                        positionType: PositionType.Future,
                        side: Side.Sell,
                        locationId: firstPosition.location.locationId,
                        contractDate: newContractDate,
                        volume: efpQuantity,
                        price: futuresPrice,
                        budgetDate: newBudgetDate,
                        unit: row.unit,
                        currency: firstPosition.currency
                    };

                    const offsettingDealTicket: UpdateDealTicketRequest = {
                        tradeDate: dayjs().format(),
                        commodityId: firstPosition.commodity.commodityId,
                        counterpartyId: firstPosition.counterparty.counterpartyId,
                        positions: [offsettingFuture],
                    };
                    await CommodityManagementApi.addTrade(null, offsettingDealTicket);

                    // Create new physical and future positions
                    const newPhysicalPosition: UpdatePositionModel = {
                        positionId: undefined,
                        positionType: PositionType.Physical,
                        side: Side.Buy,
                        locationId: selectedAllocation.location.locationId,
                        contractDate: newContractDate,
                        volume: efpQuantity,
                        price: efpPrice,
                        budgetDate: newBudgetDate,
                        unit: row.unit,
                        currency: firstPosition.currency
                    };

                    const newFuturePosition: UpdatePositionModel = {
                        positionId: undefined,
                        positionType: PositionType.Future,
                        side: Side.Sell,
                        locationId: selectedAllocation.location.locationId,
                        contractDate: newContractDate,
                        volume: efpQuantity,
                        price: efpPrice,
                        budgetDate: newBudgetDate,
                        unit: row.unit,
                        currency: firstPosition.currency
                    };

                    const newFuturePosition2: UpdatePositionModel = {
                        positionId: undefined,
                        positionType: PositionType.Future,
                        side: Side.Buy,
                        locationId: selectedAllocation.location.locationId,
                        contractDate: newContractDate,
                        volume: efpQuantity,
                        price: futuresPrice,
                        budgetDate: newBudgetDate,
                        unit: row.unit,
                        currency: firstPosition.currency
                    };

                    const physicalDealTicket: UpdateDealTicketRequest = {
                        tradeDate: dayjs().format(),
                        commodityId: firstPosition.commodity.commodityId,
                        counterpartyId: selectedAllocation.counterparty.counterpartyId,
                        dealTicketNumber: purchaseOrder,
                        positions: [newPhysicalPosition, newFuturePosition, newFuturePosition2],
                    };

                    const tradeResponse = await CommodityManagementApi.addTrade(null, physicalDealTicket);
                    const { data: createdPositions } = await CommodityManagementApi.getDealTicket(null, tradeResponse.data.dealTicketId!.toString());

                    // Create new allocations for the newly created positions
                    const allocationPromises = createdPositions.dealTicket!.positions.map((p: PositionModel) => {
                        const newAllocation: UpdateAllocationRequest = {
                            allocationId: 0,
                            allocationGroupId: selectedAllocation.allocationGroupId,
                            positionId: p.positionId!,
                            volume: efpQuantity,
                        };
                        return CommodityManagementApi.addAllocation(null, newAllocation);
                    });

                    await Promise.all(allocationPromises);
                };
                promises.push(promise());
            }
        }

        if (promises.length === 0) {
            enqueueSnackbar('No EFP trades to create. Please enter a quantity and select a supplier.', { variant: 'warning' });
            setIsSaving(false);
            return;
        }

        try {
            await Promise.all(promises);
            enqueueSnackbar(`${promises.length} EFP transaction(s) created successfully.`, { variant: 'success' });
            onSuccess();
        } catch (error) {
            console.error("Failed to create EFP trades", error);
            enqueueSnackbar('An error occurred while creating EFP trades.', { variant: 'error' });
        } finally {
            setIsSaving(false);
            onClose();
        }
    };

    const cells = (row: any, col: any, prop: any) => {
        const cellProperties = {} as any;

        if (isSaving) {
            cellProperties.readOnly = true;
        }

        return cellProperties;
    };

    function handleAfterChange(changes: CellChange[] | null, source: ChangeSource) {
        if (changes && source !== 'loadData') {
            setRowData(prev => {
                const updated = [...prev];
                changes.forEach(([rowIndex, prop, oldValue, newValue]) => {
                    const valueAsString: string = Array.isArray(newValue) ? (newValue[0]?.toString() ?? '') : (newValue?.toString() ?? '');

                    if (prop === 'efpQuantity') {
                        const maxQuantity = updated[rowIndex].openVolume;
                        const numericValue = valueAsString === '' ? '' : Number(valueAsString);
                        if (numericValue === '' || (typeof numericValue === 'number' && numericValue >= 0 && numericValue <= maxQuantity)) {
                            updated[rowIndex] = { ...updated[rowIndex], efpQuantity: numericValue };
                        }
                    } else if (prop === 'efpPrice') {
                        updated[rowIndex] = { ...updated[rowIndex], efpPrice: valueAsString === '' ? '' : Number(valueAsString) };
                    } else if (prop === 'budgetDate.month') {
                        updated[rowIndex] = {
                            ...updated[rowIndex],
                            budgetDate: { ...updated[rowIndex].budgetDate, month: valueAsString }
                        };
                    } else if (prop === 'budgetDate.year') {
                        updated[rowIndex] = {
                            ...updated[rowIndex],
                            budgetDate: { ...updated[rowIndex].budgetDate, year: valueAsString === '' ? '' : Number(valueAsString) }
                        };
                    } else if (prop === 'supplier') {
                        updated[rowIndex] = { ...updated[rowIndex], supplier: valueAsString };
                    } else if (prop === 'purchaseOrder') {
                        updated[rowIndex] = { ...updated[rowIndex], purchaseOrder: valueAsString };
                    } else if (prop === 'unit') {
                        const newUnit = valueAsString as Unit;
                        const previousUnit = updated[rowIndex].unit;
                        if (!newUnit || !sortedUnitOptions.includes(newUnit) || newUnit === previousUnit) {
                            return;
                        }

                        const row = updated[rowIndex];
                        const bushelVolume = row.bushelVolume ?? undefined;
                        const newOpenVolume = convertVolume(row.openVolume, previousUnit, newUnit, bushelVolume);
                        const newWeightedAveragePrice = convertPrice(row.weightedAveragePrice, previousUnit, newUnit, bushelVolume);
                        const newEfpQuantity = typeof row.efpQuantity === 'number'
                            ? convertVolume(row.efpQuantity, previousUnit, newUnit, bushelVolume)
                            : row.efpQuantity;
                        const newEfpPrice = typeof row.efpPrice === 'number'
                            ? convertPrice(row.efpPrice, previousUnit, newUnit, bushelVolume)
                            : row.efpPrice;

                        updated[rowIndex] = {
                            ...row,
                            unit: newUnit,
                            openVolume: newOpenVolume,
                            weightedAveragePrice: newWeightedAveragePrice,
                            efpQuantity: newEfpQuantity,
                            efpPrice: newEfpPrice
                        };
                    }
                });
                return updated;
            });
        }
    }

    const colHeaders = [
        "Contract<br /> Month",
        "Open<br /> Volume",
        "Wtd Avg<br /> Price",
        "Unit",
        "EFP<br /> Quantity",
        "EFP<br /> Price",
        "Budget<br /> Month",
        "Budget<br /> Year",
        "Supplier",
        "Purchase<br /> Order"
    ];

    // No-op handlers for PositionTable (read-only display)
    const handleSetPositionValidation = () => {};
    const handleEditPosition = () => {};
    const handleViewAuditTrail = () => {};

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>EFP Futures Positions</DialogTitle>
            <DialogContent>
                {/* EFP Entry Table - At the Top */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>EFP Entry</Typography>
                    <HotTable
                        ref={hotRef}
                        data={rowData}
                        stretchH="all"
                        width="100%"
                        height="auto"
                        colHeaders={colHeaders}
                        columns={columns}
                        autoWrapRow={true}
                        autoWrapCol={true}
                        rowHeights={40}
                        columnHeaderHeight={48}
                        minSpareRows={0}
                        cells={cells}
                        afterChange={handleAfterChange}
                        licenseKey="29ae0-f35ad-79c41-2472d-2c70d"
                    />
                </Box>

                {/* Long Futures Positions (Buy Side) */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold'}}>Long Positions</Typography>
                    <Box sx={{ height: 250 }}>
                        <PositionTable
                            customerSettings={customerSettings}
                            positions={buyFuturesPositions}
                            columnsToShow={positionTableColumns}
                            onSetPositionValidation={handleSetPositionValidation}
                            onEditPosition={handleEditPosition}
                            onViewAuditTrail={handleViewAuditTrail}
                        />
                    </Box>
                </Box>

                {/* Short Futures Positions (Sell Side) */}
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 15 }}>Short Positions</Typography>
                    <Box sx={{ height: 250 }}>
                        <PositionTable
                            customerSettings={customerSettings}
                            positions={sellFuturesPositions}
                            columnsToShow={positionTableColumns}
                            onSetPositionValidation={handleSetPositionValidation}
                            onEditPosition={handleEditPosition}
                            onViewAuditTrail={handleViewAuditTrail}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button variant="outlined" onClick={handleProceed} disabled={isSaving}>{isSaving ? 'Processing...' : 'Proceed'}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EFPPositionsModal;